import isEmpty from 'lodash/fp/isEmpty';
import isUndefined from 'lodash/fp/isUndefined';
import round from 'lodash/fp/round';
import moment from 'moment';
import { getIntl } from '../configuration/setup/intl';
import {
    BATTERY_BOTTOM_THRESHOLD,
    BATTERY_TOP_THRESHOLD,
} from '../features/livemonitorCommunication/MapRouteRendering';
import type { GenerateTourIsolineApiArg } from '../store/facade/facadeApiV2';
import type { Address as AddressV3 } from '../store/facade/facadeApiV3';
import type { Coordinates, Coords } from './types';

export const coordinatesToCoords = (position: Coordinates): Coords => ({
    lat: position.latitude,
    lng: position.longitude,
});

export const coordsToCoordinates = (position: Coords): Coordinates => ({
    latitude: position.lat,
    longitude: position.lng,
});

export const coordinatesToString = (position: Coords): string => `${position.lat}, ${position.lng}`;

export const mapUnits = (value: number | undefined, mapFn: (v: number) => number) =>
    value ? round(mapFn(value)) : undefined;

export const convertMetersIntoKilometers = (meters?: number) => (meters ? round(meters / 1000) : meters);

export const convert24hIntoMinutes = (timeValue: string) => {
    const time = moment(`2020-01-01 ${timeValue}`);
    return time.hours() * 60 + time.minutes();
};

export const convertMinutesIntoHHmm = (timeValue: number | undefined) => {
    if (timeValue === undefined) {
        return undefined;
    }

    const hours = Math.floor(timeValue / 60);
    const minutes = round(timeValue % 60);
    const h = String(hours).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');

    return `${h}h ${m}min`;
};

export const convertSecondsIntoHHmmDisplay = (timeValue?: number) => {
    if (timeValue === undefined) {
        return undefined;
    }

    const hours = (timeValue - (timeValue % 3600)) / 3600;
    const minutes = ((timeValue - (timeValue % 60)) / 60) % 60;

    let timeDisplay = '';

    if (hours !== 0) {
        timeDisplay = timeDisplay.concat(`${hours}h`, ' ');
    }
    timeDisplay = timeDisplay.concat(`${minutes}min`);

    return timeDisplay;
};

export const convertSecondsIntoHHmm = (secondsInput: string | number) => {
    const d = typeof secondsInput === 'string' ? Number(secondsInput) : secondsInput;
    const h = Math.floor(d / 3600);
    const m = Math.floor((d % 3600) / 60);

    const hours = String(h).padStart(2, '0');
    const minutes = String(m).padStart(2, '0');
    return `${hours}:${minutes}`;
};

export const isTomorrow = (date: Date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toDateString() === date.toDateString();
};

export const isToday = (date: Date) => {
    const today = new Date();
    return (
        today.getFullYear() === date.getFullYear() &&
        today.getMonth() === date.getMonth() &&
        today.getDate() === date.getDate()
    );
};

export const convertISOStringIntoDateHHmm = (ISOString?: string) => {
    if (!ISOString) {
        return undefined;
    }

    const date = new Date(ISOString);

    let convertedDate = '';
    if (isToday(date)) {
        convertedDate = 'Today';
    } else if (isTomorrow(date)) {
        convertedDate = 'Tomorrow';
    } else {
        convertedDate = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
    }

    const requestHours = String(date.getUTCHours()).padStart(2, '0');
    const requestMinutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${convertedDate}, ${requestHours}:${requestMinutes}`;
};

export const convertStringToDateFormat = (stringDate?: string) => {
    if (stringDate === undefined) {
        return null;
    }
    return new Date(stringDate).toLocaleString('en-US', { timeZone: 'UTC', hour12: false });
};

export const getIntlFormatDate = (locale: string, dateTime: Date) =>
    new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(dateTime);

export const getIntlFormaTime = (locale: string, dateTime: Date) =>
    new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(dateTime);

export const getMergeDateTime = (date: string, time: string) => {
    let day: number;
    let month: number;
    let year: number;

    if (date.includes('-')) {
        // YYYY-MM-DD format
        const [y, m, d] = date.split('-');
        year = Number(y);
        month = Number(m) - 1;
        day = Number(d);
    } else {
        // DD/MM/YYYY format
        const [d, m, y] = date.split('/');
        day = Number(d);
        month = Number(m) - 1;
        year = Number(y);
    }

    const currDate = new Date(year, month, day);

    currDate.setHours(Number(time.split(':')[0]));
    currDate.setMinutes(Number(time.split(':')[1]));
    currDate.setSeconds(0);
    currDate.setMilliseconds(0);

    return currDate.toISOString();
};

export const convertISOToRelativeDate = (locale: string, ISOString?: string) => {
    if (!ISOString) {
        return undefined;
    }

    const formatTimeAgo = (date: number) => {
        const divisions = [
            { amount: 60, name: 'seconds' },
            { amount: 60, name: 'minutes' },
            { amount: 24, name: 'hours' },
            { amount: 7, name: 'days' },
            { amount: 4.34524, name: 'weeks' },
            { amount: 12, name: 'months' },
            { amount: Number.POSITIVE_INFINITY, name: 'years' },
        ];

        const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
        let duration = (date - Date.now()) / 1000;

        for (const division of divisions) {
            if (Math.abs(duration) < division.amount) {
                return formatter.format(round(duration), division.name as Intl.RelativeTimeFormatUnit);
            }
            duration /= division.amount;
        }
    };

    // For date-only strings (YYYY-MM-DD), parse as local midnight to avoid UTC timezone shifts.
    // e.g. "2026-09-20" parsed as UTC would shift to "Sep 19" in UTC-5.
    // For date-only inputs, use isToday/isTomorrow directly instead of relative time formatting,
    // since formatTimeAgo operates on timestamps and a date-only "today" at midnight would
    // return "X hours ago" rather than "today".
    if (!ISOString.includes('T')) {
        const [y, m, d] = ISOString.split('-');
        const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
        if (isToday(dateObj)) {
            return 'Today';
        }
        if (isTomorrow(dateObj)) {
            return 'Tomorrow';
        }
        return new Intl.DateTimeFormat(locale, {
            month: '2-digit',
            day: '2-digit',
        }).format(dateObj);
    }

    const dateObj = new Date(ISOString);
    const formatedTimeAgo = formatTimeAgo(dateObj.getTime());
    let dateString: string;
    if (formatedTimeAgo === 'now') {
        dateString = 'Today';
    } else if (formatedTimeAgo === 'tomorrow') {
        dateString = 'Tomorrow';
    } else {
        dateString = new Intl.DateTimeFormat(locale, {
            month: '2-digit',
            day: '2-digit',
        }).format(dateObj);
    }

    const timeString: string = getIntlFormaTime(locale, dateObj);
    return `${dateString}, ${timeString}`;
};

export type WaypointReference = GenerateTourIsolineApiArg['reference'];
export const getWaypointReference = (waypointId: number, waypoints: number[]): WaypointReference | null => {
    const idx = waypoints.indexOf(waypointId);
    switch (idx) {
        case -1:
            return null;
        case 0:
            return 'ORIGIN';
        case waypoints.length - 1:
            return 'DESTINATION';
        default:
            return 'WAYPOINT';
    }
};

export const getFullAddressLabel = (address: AddressV3 | undefined): string | undefined => {
    if (isUndefined(address)) {
        return;
    }

    const { street, number: houseNumber, city, countryCode, postalCode, name } = address;

    const streetAndNumber = [street, houseNumber].filter(it => !isEmpty(it)).join(' ');
    const locationPart = !isEmpty(streetAndNumber) ? streetAndNumber : name;

    return [locationPart, postalCode, city, countryCode].filter(it => !isEmpty(it)).join(', ');
};

export const getIntlFormattedCurrency = (value: number | undefined, fractionDigits = 0) => {
    if (isUndefined(value)) {
        return '-';
    }

    const intl = getIntl();
    return intl.formatNumber(value, {
        currency: 'EUR',
        style: 'currency',
        notation: 'standard',
        maximumFractionDigits: fractionDigits,
    });
};

export const getBatteryIcon = (value: number | undefined) => {
    if (value) {
        if (value >= 0 && value <= 20) {
            return 'rioglyph rioglyph-battery-level-empty';
        }
        if (value > 20 && value < 80) {
            return 'rioglyph rioglyph-battery-level-low';
        }
        if (value >= 80 && value <= 100) {
            return 'rioglyph rioglyph-battery-level-full';
        }
        return 'rioglyph rioglyph-battery-level-empty';
    }
    return 'rioglyph rioglyph-battery-level-empty';
};

export const getBatteryColor = (value: number | undefined) => {
    if (value) {
        if (value <= BATTERY_BOTTOM_THRESHOLD) {
            return 'text-color-danger';
        }
        if (value > BATTERY_BOTTOM_THRESHOLD && value < BATTERY_TOP_THRESHOLD) {
            return 'text-color-warning';
        }
        if (value >= BATTERY_TOP_THRESHOLD) {
            return 'text-color-success';
        }
    } else {
        return 'text-color-danger';
    }
};

export const delay = (delayInMillis: number) => new Promise(f => setTimeout(f, delayInMillis));

export const oneOf = <T>(value: T | undefined, ...candidates: T[]): boolean =>
    value !== undefined && candidates.includes(value);

export const allOf = <T>(value: T | undefined, ...candidates: T[]): boolean =>
    value !== undefined && candidates.length > 0 && candidates.every(c => c === value);
